import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Grid,
  Paper,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon, 
  Save as SaveIcon
} from '@mui/icons-material';
import { api } from '../utils/api';
import toast from 'react-hot-toast';

const SiteForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [purgeTypes, setPurgeTypes] = useState([]);

  
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      domain: '',
      service_id: '',
      description: '',
    }
  });

  const watchedValues = watch();

  useEffect(() => {
    if (isEdit) {
      loadSite();
    }
  }, [id, isEdit]);

  const loadSite = async () => {
    try {
      setInitialLoading(true);
      const site = await api.sites.get(id);
      
      // Set form values
      setValue('name', site.name);
      setValue('domain', site.domain);
      setValue('service_id', site.service_id);
      setValue('description', site.description || '');
      
      // Set purge types
      setPurgeTypes(site.purge_types);
    } catch (error) {
      toast.error('Failed to load site');
      navigate('/sites');
    } finally {
      setInitialLoading(false);
    }
  };

  const onSubmit = async (data) => {
    if (purgeTypes.length === 0) {
      toast.error('Please select at least one purge type');
      return;
    }

    setLoading(true);
    try {
      const siteData = {
        ...data,
        fastly_token: 'SERVER_MANAGED', // Use server-managed token
        purge_types: purgeTypes,
      };

      if (isEdit) {
        await api.sites.update(id, siteData);
        toast.success('Site updated successfully');
      } else {
        await api.sites.create(siteData);
        toast.success('Site created successfully');
      }
      
      navigate('/sites');
    } catch (error) {
      toast.error(error.message || `Failed to ${isEdit ? 'update' : 'create'} site`);
    } finally {
      setLoading(false);
    }
  };

  const handlePurgeTypeChange = (type, checked) => {
    if (checked) {
      setPurgeTypes(prev => [...prev, type]);
    } else {
      setPurgeTypes(prev => prev.filter(t => t !== type));
    }
  };



  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/sites')}
          sx={{ mr: 2 }}
        >
          Back to Sites
        </Button>
        <Typography variant="h4" fontWeight={600}>
          {isEdit ? 'Edit Site' : 'Add New Site'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Site Name"
                      placeholder="My Website"
                      {...register('name', {
                        required: 'Site name is required',
                        minLength: {
                          value: 1,
                          message: 'Site name cannot be empty',
                        },
                      })}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Domain"
                      placeholder="www.example.com"
                      {...register('domain', {
                        required: 'Domain is required',
                        pattern: {
                          value: /^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)*$/,
                          message: 'Please enter a valid domain name',
                        },
                      })}
                      error={!!errors.domain}
                      helperText={errors.domain?.message || 'The domain serving the content (e.g., www.example.com)'}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Fastly Service ID"
                      placeholder="1a2b3c4d5e6f7g8h"
                      {...register('service_id', {
                        required: 'Service ID is required',
                        minLength: {
                          value: 1,
                          message: 'Service ID cannot be empty',
                        },
                      })}
                      error={!!errors.service_id}
                      helperText={errors.service_id?.message || 'Your Fastly service ID from the Fastly dashboard'}
                    />
                  </Grid>





                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      placeholder="Optional description of this site"
                      multiline
                      rows={3}
                      {...register('description')}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl component="fieldset" variant="standard">
                      <FormLabel component="legend">
                        Allowed Purge Types *
                      </FormLabel>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={purgeTypes.includes('url')}
                              onChange={(e) => handlePurgeTypeChange('url', e.target.checked)}
                            />
                          }
                          label={
                            <Box>
                              <Typography component="span">URL Specific Purging</Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                Purge individual pages or files by exact URL
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={purgeTypes.includes('surrogate_key')}
                              onChange={(e) => handlePurgeTypeChange('surrogate_key', e.target.checked)}
                            />
                          }
                          label={
                            <Box>
                              <Typography component="span">Surrogate Key Purging</Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                Purge all pages tagged with a specific key (e.g., domain name)
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={purgeTypes.includes('purge_all')}
                              onChange={(e) => handlePurgeTypeChange('purge_all', e.target.checked)}
                            />
                          }
                          label={
                            <Box>
                              <Typography component="span">Purge All Cache</Typography>
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ color: 'warning.main' }}>
                                ⚠️ Purges ALL content for ALL domains in the Fastly service
                              </Typography>
                            </Box>
                          }
                        />
                      </FormGroup>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {loading ? 'Saving...' : (isEdit ? 'Update Site' : 'Create Site')}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Configuration Guide
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Domain:</strong> Enter the exact domain that serves your content. 
              For surrogate key purging to work, this domain must include surrogate key headers in responses.
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Service ID:</strong> Found in your Fastly dashboard. For easy setup, 
              administrators can import services directly from the "Fastly Services" page.
            </Typography>
            
            <Alert severity="info" sx={{ my: 2 }}>
              <Typography variant="body2">
                <strong>API Tokens:</strong> Tokens are now managed centrally by administrators 
                for better security. Users don't need to provide individual tokens.
              </Typography>
            </Alert>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Surrogate Key Note:</strong> If your domain redirects (e.g., example.com → www.example.com), 
                use the final destination domain as surrogate keys are only present in the actual response headers.
              </Typography>
            </Alert>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SiteForm;
